import { useEffect, useState } from "react";
import { Save, Plus, Trash2, MessageSquareQuote } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Pagination, usePageSlice } from "@/components/Pagination";
import { toast } from "sonner";

const PER_PAGE = 5;

export default function AdminTestimonials() {
  const [whatsapp, setWhatsapp] = useState("");
  const [banners, setBanners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setWhatsapp(r.data.whatsapp_number || "");
      setBanners(r.data.banners || []);
      setTestimonials(r.data.testimonials || []);
    }).catch(() => {});
  }, []);

  const { paged, totalPages, start } = usePageSlice(testimonials, page, PER_PAGE);

  const update = (i, key, val) => setTestimonials((t) => t.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  const remove = (i) => setTestimonials((t) => t.filter((_, idx) => idx !== i));
  const add = () => { setTestimonials((t) => [...t, { quote: "", author: "" }]); setPage(Math.ceil((testimonials.length + 1) / PER_PAGE)); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { whatsapp_number: whatsapp.trim(), banners, testimonials });
      toast.success("Testimonials saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-2xl font-bold mb-1">Testimonials</h1>
      <p className="text-sm text-muted-foreground mb-6">Reviews shown in the "What our customers say" section. Remove all to hide the section.</p>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">{testimonials.length} review(s)</span>
        <button onClick={add} className="inline-flex items-center gap-1.5 border border-border rounded-full px-3 py-1.5 text-xs font-medium" data-testid="add-testimonial-btn"><Plus className="w-3.5 h-3.5" /> Add Review</button>
      </div>

      <div className="space-y-4">
        {paged.map((t, idx) => {
          const i = start + idx;
          return (
            <div key={i} className="bg-card border border-border rounded-xl p-4" data-testid={`testimonial-row-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><MessageSquareQuote className="w-3.5 h-3.5 text-primary" /> Review {i + 1}</span>
                <button onClick={() => remove(i)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`testimonial-del-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={t.quote || ""} onChange={(e) => update(i, "quote", e.target.value)} placeholder="Customer quote" rows={2} data-testid={`testimonial-quote-${i}`}
                className="w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm text-foreground outline-none focus:border-primary" />
              <input value={t.author || ""} onChange={(e) => update(i, "author", e.target.value)} placeholder="Author (e.g. Priya, Mumbai)" data-testid={`testimonial-author-${i}`}
                className="mt-2 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm text-foreground outline-none focus:border-primary" />
            </div>
          );
        })}
        {testimonials.length === 0 && <p className="text-sm text-muted-foreground">No testimonials yet. Add one to show the section.</p>}
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />

      <button onClick={save} disabled={saving} className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="save-testimonials-btn">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Testimonials"}
      </button>
    </div>
  );
}
